export enum XrefColumnType {
  FROM,
  TO,
  OTHER,
}

export interface XrefColumnInfo {
  columnName?: string;
  userReadableName?: string;
  columnHeaderName?: string;
  visible?: boolean;
  defaultVisible?: boolean;
  canBeRemoved?: boolean;
  columnType?: XrefColumnType;
  filterable?: boolean;

  externalAllowed?: boolean;
  order?: number;
  key?: string;
}

export interface XrefColumnDisplayOrder {
  key: string;
  externalAllowed: boolean;
  order: number;
  defaultVisible: boolean;
}
export interface XrefColumnRename {
  key: string;
  newUserReadableName: string;
  newColumnHeaderName: string;
}

export class XrefColumnData {
  public fromPim: XrefColumnInfo;
  public fromName: XrefColumnInfo;
  public fromPartNumber: XrefColumnInfo;
  public fromDesc: XrefColumnInfo;
  public fromUNSPSCCode: XrefColumnInfo;
  public fromUNSPSCDesc: XrefColumnInfo;
  public fromUNSPSC: XrefColumnInfo;
  public fromItemType: XrefColumnInfo;
  public fromDiscontinuedFlag: XrefColumnInfo;
  public fromNovaPlusFlag: XrefColumnInfo;
  public toPim: XrefColumnInfo;
  public toName: XrefColumnInfo;
  public toPartNumber: XrefColumnInfo;
  public toDesc: XrefColumnInfo;
  public toUNSPSCCode: XrefColumnInfo;
  public toUNSPSCDesc: XrefColumnInfo;
  public toUNSPSC: XrefColumnInfo;
  public toItemType: XrefColumnInfo;
  public toDiscontinuedFlag: XrefColumnInfo;
  public toNovaPlusFlag: XrefColumnInfo;
  public confidenceScore: XrefColumnInfo;
  public fileBatch: XrefColumnInfo;
  public sourceType: XrefColumnInfo;
  public isValid: XrefColumnInfo;
  public validationNote: XrefColumnInfo;
  public createDTM: XrefColumnInfo;
  public fromPsc: XrefColumnInfo;
  public toPsc: XrefColumnInfo;
  public fromSyncDesc: XrefColumnInfo;
  public toSyncDesc: XrefColumnInfo;
  public fromSyncGroupDesc: XrefColumnInfo;
  public toSyncGroupDesc: XrefColumnInfo;
  public fromContrNum: XrefColumnInfo;
  public toContrNum: XrefColumnInfo;
  public isReviewed: XrefColumnInfo;
  public toNationalContractFlag: XrefColumnInfo;
  public fromNationalContractFlag: XrefColumnInfo;
  public categorizationRank: XrefColumnInfo;
  public sourceCount: XrefColumnInfo;
  public toSyncCode: XrefColumnInfo;
  public fromSyncCode: XrefColumnInfo;
}

export class DisplayableXrefColumns {
  public XrefColumns: XrefColumnData = new XrefColumnData();

  private defaultXrefColumnData: XrefColumnInfo = {
    defaultVisible: false,
    canBeRemoved: true,
    filterable: true,
    externalAllowed: false,
    order: -1,
  };

  public constructor() {
    //#region Set up data for columns
    this.XrefColumns.fromPim = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'fromPim',
      userReadableName: 'Input Product Key',
      columnHeaderName: 'Product Key',
      columnType: XrefColumnType.FROM,
    });
    this.XrefColumns.fromName = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'fromName',
      userReadableName: 'Input Vendor Name',
      columnHeaderName: 'Vendor Name',
      columnType: XrefColumnType.FROM,
    });
    this.XrefColumns.fromPartNumber = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        canBeRemoved: false,
        columnName: 'fromPartNumber',
        userReadableName: 'Input Vendor Part Number',
        columnHeaderName: 'Vendor Part Number',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromDesc = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'fromDesc',
      userReadableName: 'Input Vendor Product Description',
      columnHeaderName: 'Vendor Product Description',
      columnType: XrefColumnType.FROM,
    });
    this.XrefColumns.fromPsc = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'fromPsc',
      userReadableName: 'Input Vizient Contract Category',
      columnHeaderName: 'Vizient Contract Category',
      columnType: XrefColumnType.FROM,
    });
    this.XrefColumns.fromUNSPSC = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromUNSPSC',
        userReadableName: 'Input UNSPSC Category',
        columnHeaderName: 'UNSPSC Category',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromUNSPSCCode = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromUNSPSCCode',
        userReadableName: 'Input UNSPSC Code',
        columnHeaderName: 'UNSPSC Code',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromUNSPSCDesc = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromUNSPSCDesc',
        userReadableName: 'Input UNSPSC Description',
        columnHeaderName: 'UNSPSC Description',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromNovaPlusFlag = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromNovaPlusFlag',
        userReadableName: 'Input NovaPlus Flag',
        columnHeaderName: 'NovaPlus Flag',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromSyncDesc = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromSyncDesc',
        userReadableName: 'Input VSC Subcategory Description',
        columnHeaderName: 'VSC Subcategory Description',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromSyncGroupDesc = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromSyncGroupDesc',
        userReadableName: 'Input VSC Group Description',
        columnHeaderName: 'VSC Group Description',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromContrNum = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromContrNum',
        userReadableName: 'Input Contract Number',
        columnHeaderName: 'Contract Number',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromItemType = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromItemType',
        userReadableName: 'Input MFG or Dist Type',
        columnHeaderName: 'MFG or Dist Type',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.fromDiscontinuedFlag = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromDiscontinuedFlag',
        userReadableName: 'Input Discontinued Flag',
        columnHeaderName: 'Discontinued Flag',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.toDiscontinuedFlag = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toDiscontinuedFlag',
        userReadableName: 'Output Discontinued Flag',
        columnHeaderName: 'Discontinued Flag',
        columnType: XrefColumnType.TO,
        filterable: false,
      },
    );
    this.XrefColumns.toPim = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'toPim',
      userReadableName: 'Output Product Key',
      columnHeaderName: 'Product Key',
      columnType: XrefColumnType.TO,
    });
    this.XrefColumns.toNovaPlusFlag = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toNovaPlusFlag',
        userReadableName: 'Output NovaPlus Flag',
        columnHeaderName: 'NovaPlus Flag',
        columnType: XrefColumnType.TO,
        filterable: false,
      },
    );
    this.XrefColumns.toName = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'toName',
      userReadableName: 'Output Vendor Name',
      columnHeaderName: 'Vendor Name',
      columnType: XrefColumnType.TO,
    });
    this.XrefColumns.toPartNumber = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        canBeRemoved: false,
        columnName: 'toPartNumber',
        userReadableName: 'Output Vendor Part Number',
        columnHeaderName: 'Vendor Part Number',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.toDesc = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'toDesc',
      userReadableName: 'Output Vendor Product Description',
      columnHeaderName: 'Vendor Product Description',
      columnType: XrefColumnType.TO,
    });
    this.XrefColumns.toPsc = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'toPsc',
      userReadableName: 'Output Vizient Contract Category',
      columnHeaderName: 'Vizient Contract Category',
      columnType: XrefColumnType.TO,
    });
    this.XrefColumns.toUNSPSC = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'toUNSPSC',
      userReadableName: 'Output UNSPSC Category',
      columnHeaderName: 'UNSPSC Category',
      columnType: XrefColumnType.TO,
    });
    this.XrefColumns.toUNSPSCCode = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toUNSPSCCode',
        userReadableName: 'Output UNSPSC Code',
        columnHeaderName: 'UNSPSC Code',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.toUNSPSCDesc = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toUNSPSCDesc',
        userReadableName: 'Output UNSPSC Description',
        columnHeaderName: 'UNSPSC Description',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.toSyncDesc = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toSyncDesc',
        userReadableName: 'Output VSC Subcategory Description',
        columnHeaderName: 'VSC Subcategory Description',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.toSyncGroupDesc = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toSyncGroupDesc',
        userReadableName: 'Output VSC Group Description',
        columnHeaderName: 'VSC Group Description',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.toContrNum = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toContrNum',
        userReadableName: 'Output Contract Number',
        columnHeaderName: 'Contract Number',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.toItemType = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toItemType',
        userReadableName: 'Output MFG or Dist Type',
        columnHeaderName: 'MFG or Dist Type',
        columnType: XrefColumnType.TO,
        filterable: false,
      },
    );
    this.XrefColumns.confidenceScore = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'confidenceScore',
        userReadableName: 'Confidence Label',
        columnHeaderName: 'Confidence Label',
        columnType: XrefColumnType.OTHER,
      },
    );
    this.XrefColumns.fileBatch = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'fileBatch',
      userReadableName: 'Batch',
      columnHeaderName: 'Batch',
      columnType: XrefColumnType.OTHER,
    });
    this.XrefColumns.sourceType = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'sourceType',
        userReadableName: 'Source',
        columnHeaderName: 'Source',
        columnType: XrefColumnType.OTHER,
      },
    );
    this.XrefColumns.isValid = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'isValid',
      userReadableName: 'Is Valid',
      columnHeaderName: 'Is Valid',
      columnType: XrefColumnType.OTHER,
      filterable: false,
    });
    this.XrefColumns.isReviewed = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'isReviewed',
        userReadableName: 'Reviewed',
        columnHeaderName: 'Reviewed',
        columnType: XrefColumnType.OTHER,
        filterable: false,
      },
    );
    this.XrefColumns.validationNote = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'validationNote',
        userReadableName: 'Validation Note',
        columnHeaderName: 'Validation Note',
        columnType: XrefColumnType.OTHER,
      },
    );
    this.XrefColumns.createDTM = Object.assign({}, this.defaultXrefColumnData, {
      columnName: 'createDTM',
      userReadableName: 'Create Date',
      columnHeaderName: 'Create Date',
      columnType: XrefColumnType.OTHER,
    });
    this.XrefColumns.fromNationalContractFlag = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromNationalContractFlag',
        userReadableName: 'Input Active on National Contract',
        columnHeaderName: 'Active on National Contract',
        columnType: XrefColumnType.FROM,
      },
    );
    this.XrefColumns.toNationalContractFlag = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toNationalContractFlag',
        userReadableName: 'Output Active on National Contract',
        columnHeaderName: 'Active on National Contract',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.categorizationRank = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        canBeRemoved: false,
        columnName: 'categorizationRank',
        userReadableName: 'Categorization Rank',
        columnHeaderName: 'Categorization Rank',
        columnType: XrefColumnType.OTHER,
      },
    );
    this.XrefColumns.sourceCount = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'sourceCount',
        userReadableName: 'Number of Sources',
        columnHeaderName: 'Number of Sources',
        columnType: XrefColumnType.OTHER,
      },
    );
    this.XrefColumns.toSyncCode = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'toSyncCode',
        userReadableName: 'Output VSC Code',
        columnHeaderName: 'VSC',
        columnType: XrefColumnType.TO,
      },
    );
    this.XrefColumns.fromSyncCode = Object.assign(
      {},
      this.defaultXrefColumnData,
      {
        columnName: 'fromSyncCode',
        userReadableName: 'Input VSC Code',
        columnHeaderName: 'VSC',
        columnType: XrefColumnType.FROM,
      },
    );
    //#endregion
    Object.keys(this.XrefColumns).forEach(k => {
      this.XrefColumns[k].key = k;
    });

    this.loadDisplayedColumns();
  }
  public getXrefAvailableColumnKeys(externalOnly: boolean = false): string[] {
    let result: string[] = [];
    Object.keys(this.XrefColumns)
      .filter(
        k =>
          (!externalOnly || this.XrefColumns[k].externalAllowed) &&
          this.XrefColumns[k].order > -1,
      )
      .sort((k1, k2) => {
        return this.XrefColumns[k1].order - this.XrefColumns[k2].order;
      })
      .forEach(key => {
        result.push(key);
      });
    return result;
  }
  public getXrefAvailableColumns(
    externalOnly: boolean = false,
  ): XrefColumnInfo[] {
    let result: XrefColumnInfo[] = [];
    Object.keys(this.XrefColumns)
      .filter(
        k =>
          (!externalOnly || this.XrefColumns[k].externalAllowed) &&
          this.XrefColumns[k].order > -1,
      )
      .sort((k1, k2) => {
        return this.XrefColumns[k1].order - this.XrefColumns[k2].order;
      })
      .forEach(key => {
        result.push(this.XrefColumns[key]);
      });
    return result;
  }
  public getXrefDisplayedColumnKeys(externalOnly: boolean = false): string[] {
    let result: string[] = [];
    Object.keys(this.XrefColumns)
      .filter(
        key =>
          (!externalOnly || this.XrefColumns[key].externalAllowed) &&
          this.XrefColumns[key].visible,
      )
      .sort((k1, k2) => {
        return this.XrefColumns[k1].order - this.XrefColumns[k2].order;
      })
      .forEach(key => {
        result.push(key);
      });
    return result;
  }
  public firstToColumnName(externalOnly: boolean = false): string {
    let result = '';
    this.getXrefDisplayedColumnKeys(externalOnly).forEach(k => {
      if (this.XrefColumns[k].columnType == XrefColumnType.TO && result == '') {
        result = this.XrefColumns[k].key;
      }
    });
    return result;
  }
  public firstOtherColumnName(externalOnly: boolean = false): string {
    let result = '';
    this.getXrefDisplayedColumnKeys(externalOnly).forEach(k => {
      if (
        this.XrefColumns[k].columnType == XrefColumnType.OTHER &&
        result == ''
      ) {
        result = this.XrefColumns[k].key;
      }
    });
    return result;
  }
  public fromColumnCount(externalOnly: boolean = false): number {
    let result = [];
    this.getXrefDisplayedColumnKeys(externalOnly)
      .filter(k => this.XrefColumns[k].columnType == XrefColumnType.FROM)
      .forEach(k => result.push(k));
    return result.length;
  }
  public toColumnCount(externalOnly: boolean = false): number {
    let result = [];
    this.getXrefDisplayedColumnKeys(externalOnly)
      .filter(k => this.XrefColumns[k].columnType == XrefColumnType.TO)
      .forEach(k => result.push(k));
    return result.length;
  }
  public otherColumnCount(externalOnly: boolean = false): number {
    let result = [];
    this.getXrefDisplayedColumnKeys(externalOnly)
      .filter(k => this.XrefColumns[k].columnType == XrefColumnType.OTHER)
      .forEach(k => result.push(k));
    return result.length;
  }
  public saveDisplayedColumns(): void {
    let saveName = window.location.pathname.slice(1) + '-ColumnSettings';
    let data = [];
    this.getXrefAvailableColumns().forEach(c =>
      data.push({ key: c.key, visible: c.visible }),
    );
    localStorage.setItem(saveName, JSON.stringify({ columnData: data }));
  }
  public loadDisplayedColumns(): void {
    this.loadDefaultDisplayedColumns();
    let saveName = window.location.pathname.slice(1) + '-ColumnSettings';
    try {
      let data = JSON.parse(localStorage.getItem(saveName));
      data.columnData.forEach(r => {
        this.XrefColumns[r.key].visible = r.visible;
      });
    } catch (e) {
      this.loadDefaultDisplayedColumns();
    }
  }
  public loadDefaultDisplayedColumns(): void {
    this.getXrefAvailableColumnKeys().forEach(k => {
      this.XrefColumns[k].visible = this.XrefColumns[k].defaultVisible;
    });
  }
  public setXrefDisplayColumnDefaults(values: XrefColumnDisplayOrder[]): void {
    values.forEach(k => {
      this.XrefColumns[k.key].order = k.order;
      this.XrefColumns[k.key].externalAllowed = k.externalAllowed;
      this.XrefColumns[k.key].defaultVisible = k.defaultVisible;
      this.XrefColumns[k.key].visible = k.defaultVisible;
    });
    this.loadDisplayedColumns();
  }
  public renameXrefColumns(values: XrefColumnRename[]): void {
    values.forEach(k => {
      this.XrefColumns[k.key].userReadableName = k.newUserReadableName;
      this.XrefColumns[k.key].columnHeaderName = k.newColumnHeaderName;
    });
  }
}
export class DisplayPageSize {
  public savePage(pageSize): void {
    let saveName = window.location.pathname.slice(1) + '-PageSettings';
    localStorage.setItem(saveName, pageSize.toString());
  }
  public loadPageSize(): number {
    let saveName = window.location.pathname.slice(1) + '-PageSettings';
    if (localStorage.getItem(saveName) === null) {
      return 0;
    }
    return parseInt(localStorage.getItem(saveName));
  }
}
